import { z } from 'zod';

const on_duty_entry = z
  .object({
    id: z.number().int(),
    name: z.string(),
    first_name: z.string(),
    full_name: z.string(),
    phone: z.string().nullable(),
    starts_at: z.string().datetime({ offset: true }),
    ends_at: z.string().datetime({ offset: true }),
    on_duty: z.boolean(),
  })
  .passthrough();
const on_duty_meta = z
  .object({
    date: z.string(),
    time_zone: z.string(),
    fallback_email: z.string(),
  })
  .passthrough();
const on_duty_response = z
  .object({
    success: z.boolean(),
    data: z.array(on_duty_entry),
    meta: on_duty_meta,
  })
  .passthrough();
const error_response = z
  .object({ success: z.boolean(), errors: z.array(z.string()) })
  .passthrough();
const auth_error_response = z.object({ error: z.string() }).passthrough();
const forbidden_response = z.union([auth_error_response, error_response]);
const manager_entry = z
  .object({
    id: z.number().int(),
    first_name: z.string(),
    last_name: z.string(),
    full_name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    active: z.boolean(),
    tenant_admin_id: z.number().int().nullable(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
  })
  .passthrough();
const managers_response = z
  .object({ success: z.boolean(), data: z.array(manager_entry) })
  .passthrough();
const manager_input = z
  .object({
    first_name: z.string().max(128),
    last_name: z.string().max(128),
    phone: z.string().max(32).nullish(),
    email: z.string().max(255).nullish(),
    tenant_admin_id: z.number().int().nullish(),
    agency_id: z.number().int().optional(),
  })
  .passthrough();
const manager_response = z
  .object({ success: z.boolean(), data: manager_entry })
  .passthrough();
const manager_update_input = z
  .object({
    first_name: z.string().max(128),
    last_name: z.string().max(128),
    phone: z.string().max(32).nullable(),
    email: z.string().max(255).nullable(),
    active: z.boolean(),
    tenant_admin_id: z.number().int().nullable(),
  })
  .partial()
  .passthrough();
const manager_delete_response = z
  .object({
    success: z.boolean(),
    data: z.object({ id: z.number().int() }).passthrough(),
  })
  .passthrough();
const manager_summary = z
  .object({
    id: z.number().int(),
    first_name: z.string(),
    last_name: z.string(),
    full_name: z.string(),
    active: z.boolean(),
  })
  .passthrough();
const schedule = z
  .object({
    id: z.number().int(),
    date: z.string(),
    start_time: z.string(),
    end_time: z.string(),
    starts_at: z.string().datetime({ offset: true }),
    ends_at: z.string().datetime({ offset: true }),
    crosses_midnight: z.boolean(),
    lock_version: z.number().int(),
    editable: z.boolean(),
    carry_over: z.boolean(),
    managers: z.array(manager_summary),
    updated_at: z.string().datetime({ offset: true }).nullable(),
  })
  .passthrough();
const schedules_meta = z
  .object({ time_zone: z.string(), today: z.string() })
  .passthrough();
const schedules_response = z
  .object({
    success: z.boolean(),
    data: z.array(schedule),
    meta: schedules_meta,
  })
  .passthrough();
const createOpsSchedule_Body = z
  .object({
    date: z.string(),
    end_date: z.string().optional(),
    start_time: z.string(),
    end_time: z.string(),
    manager_ids: z.array(z.number().int()).max(50),
    agency_id: z.number().int().optional(),
  })
  .passthrough();
const create_schedule_response = z
  .object({
    success: z.boolean(),
    data: schedule,
    meta: z
      .object({
        merged: z.boolean(),
        dates: z.array(z.string()).min(1).max(62),
      })
      .passthrough(),
  })
  .passthrough();
const updateOpsSchedule_Body = z
  .object({
    start_time: z.string(),
    end_time: z.string(),
    manager_ids: z.array(z.number().int()).max(50),
    lock_version: z.number().int(),
  })
  .passthrough();
const schedule_response = z
  .object({
    success: z.boolean(),
    data: schedule,
    meta: z.object({ merged: z.boolean() }).passthrough(),
  })
  .passthrough();
const delete_response = z
  .object({
    success: z.boolean(),
    data: z.object({ id: z.number().int(), date: z.string() }).passthrough(),
  })
  .passthrough();
const repeatOpsSchedule_Body = z
  .object({
    dates: z.array(z.string()).min(1).max(62),
    start_time: z.string(),
    end_time: z.string(),
    manager_ids: z.array(z.number().int()).max(50),
  })
  .passthrough();
const repeat_skip = z
  .object({
    date: z.string(),
    manager_id: z.number().int(),
    full_name: z.string(),
    reason: z.string(),
  })
  .passthrough();
const repeat_response = z
  .object({
    success: z.boolean(),
    data: z
      .object({ schedules: z.array(schedule), skipped: z.array(repeat_skip) })
      .passthrough(),
  })
  .passthrough();

export const schemas = {
  on_duty_entry,
  on_duty_meta,
  on_duty_response,
  error_response,
  auth_error_response,
  forbidden_response,
  manager_entry,
  managers_response,
  manager_input,
  manager_response,
  manager_update_input,
  manager_delete_response,
  manager_summary,
  schedule,
  schedules_meta,
  schedules_response,
  createOpsSchedule_Body,
  create_schedule_response,
  updateOpsSchedule_Body,
  schedule_response,
  delete_response,
  repeatOpsSchedule_Body,
  repeat_skip,
  repeat_response,
};
